import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, RotateCcw } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { useLang } from "@/i18n/LangContext";

const PHASE_META = [
  { ms: 4000, scale: 1.35 },
  { ms: 4000, scale: 1.35 },
  { ms: 4000, scale: 1.0 },
  { ms: 2000, scale: 0.92 },
];

export default function Stillness() {
  const { t } = useLang();
  const phaseLabels = t("stillness.phases");
  const [running, setRunning] = useState(false);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const timers = useRef([]);

  useEffect(() => {
    if (!running) return;
    let i = 0;
    const tick = () => {
      setPhaseIdx(i % PHASE_META.length);
      timers.current.push(setTimeout(() => { i += 1; tick(); }, PHASE_META[i % PHASE_META.length].ms));
    };
    tick();
    const clock = setInterval(() => setElapsed((e) => e + 1), 1000);
    const stop = setTimeout(() => setRunning(false), 60000);
    timers.current.push(stop);
    return () => { clearInterval(clock); timers.current.forEach(clearTimeout); timers.current = []; };
  }, [running]);

  const meta = PHASE_META[phaseIdx];
  const done = !running && elapsed >= 60;

  return (
    <section data-testid="interactive-stillness-zone" className="px-5 py-24 sm:px-8 lg:py-32">
      <div className="mx-auto flex max-w-4xl flex-col items-center text-center">
        <Reveal>
          <Eyebrow>{t("stillness.eyebrow")}</Eyebrow>
          <h2 className="font-display mt-6 text-3xl font-light tracking-tight sm:text-4xl lg:text-5xl">
            {t("stillness.h1")}<span className="italic" style={{ color: "var(--accent-gold)" }}>{t("stillness.h2")}</span>
          </h2>
          <p className="mx-auto mt-5 max-w-lg text-base leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {t("stillness.sub")}
          </p>
        </Reveal>

        <Reveal delay={0.15} className="mt-14 flex flex-col items-center">
          <div className="relative flex h-56 w-56 items-center justify-center">
            <motion.div
              className="absolute inset-0 rounded-full border"
              style={{ borderColor: "var(--border-hover)", background: "var(--accent-glow)" }}
              animate={{ scale: running ? meta.scale : 1, opacity: running ? 0.9 : 0.5 }}
              transition={{ duration: running ? meta.ms / 1000 : 0.8, ease: "easeInOut" }}
            />
            <div className="absolute inset-6 rounded-full border" style={{ borderColor: "var(--border-delicate)" }} />
            <div className="relative z-10 px-4">
              <p className="font-display text-2xl italic" style={{ color: "var(--text-primary)" }}>
                {running ? phaseLabels[phaseIdx] : done ? t("stillness.welcomeBack") : t("stillness.ready")}
              </p>
              <p className="mt-2 font-mono-alt text-[11px] uppercase tracking-[0.3em]" style={{ color: "var(--text-muted)" }}>
                {running ? t("stillness.remain").replace("{n}", 60 - elapsed) : done ? t("stillness.silenceNote") : t("stillness.seconds")}
              </p>
            </div>
          </div>

          <button
            data-testid="breath-guide-start-button"
            onClick={() => { setElapsed(0); setPhaseIdx(0); setRunning(true); }}
            disabled={running}
            className="mt-10 flex items-center gap-2 rounded-full px-8 py-3.5 text-sm font-semibold tracking-wide transition-transform enabled:hover:scale-[1.04] disabled:opacity-50"
            style={{ backgroundColor: "var(--primary-btn-bg)", color: "var(--primary-btn-text)" }}
          >
            {done ? <RotateCcw size={15} /> : <Play size={15} />}
            {done ? t("stillness.sitAgain") : running ? t("stillness.breathing") : t("stillness.begin")}
          </button>
        </Reveal>
      </div>
    </section>
  );
}
